import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { RouterSegments } from 'src/app/models/router-segments';
import { PermissionService } from 'src/app/services/api/permission.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { ViewPermissionsComponent } from '../view-permissions/view-permissions.component';
import { AssignRoleComponent } from '../assign-role/assign-role.component';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { PermissionValues } from 'src/app/models/permission-values';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Permissions } from 'src/app/models/principal/permission';

const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.ROLE_ASSIGN_READ,
    action: Permissions.ROLE_ASSIGN_READ,
  },
  {
    key: PermissionKeys.GROUP_UPDATE,
    action: Permissions.GROUP_UPDATE,
  },
  {
    key: PermissionKeys.ROLE_ASSIGN_CREATE,
    action: Permissions.ROLE_ASSIGN_CREATE,
  },
  {
    key: PermissionKeys.ROLE_ASSIGN_UPDATE,
    action: Permissions.ROLE_ASSIGN_UPDATE,
  }
];

@Component({
    selector: 'app-group-roles',
    templateUrl: './group-roles.component.html',
    styleUrls: ['./group-roles.component.scss'],
    standalone: false
})
export class GroupRolesComponent  implements OnInit {

  RouterSegments = RouterSegments;
  breadcrumbItems: BreadcrumbItem[];
  groupName: string;
  roleAssignments: any;
  tenantRoles = [];
  group: any;
  isLoading = true;
  showLoader = false;
  PermissionKeys = PermissionKeys;
  permissionsMap: { [key: string]: boolean; };
private destroyRef = inject(DestroyRef);
  constructor (private activatedRoute: ActivatedRoute, private permissionService: PermissionService,
    private toastrService: CustomToastrService, private authService: AuthService, private router: Router,
    private dialog: MatDialog, private sidenavService: SidenavService, private permissionsService: PermissionService, private permissionRoleService: PermissionsService) {
    
  }

  ngOnInit(): void {
    this.permissionsMap = this.permissionRoleService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    if (!this.permissionsMap[PermissionKeys.ROLE_ASSIGN_READ]) {
      this.router.navigate(['/', this.authService.principal.provider, 'permission-unauthorized'], {skipLocationChange: true});
    }
    this.sidenavService.selectedMenu = 'LEFT_SIDE_NAV.GROUPS';
    this.groupName = this.activatedRoute.snapshot.params.groupName;
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({icon: 'home'}, {label: translate('LEFT_SIDE_NAV.ACCESS_MANAGEMENT')},
      {label: translate('LEFT_SIDE_NAV.GROUPS'), url: ['groups']}, {label: this.groupName});
    this.getGroup();
    this.getRoleAssignments();
  }

  getGroup(): void {
    this.permissionService.getGroupByName(this.groupName).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(group => {
      this.group = group;
    },
    (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  getRoleAssignments(): void {
    this.isLoading = true;
    const pagePayload = {
      principals: PermissionValues.GROUP + this.groupName
    }
    this.permissionService.getRoleAssignments(pagePayload).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      if (data.total > 10) {
        const payload = {
          principals: PermissionValues.GROUP + this.groupName,
          size: data.total
        };
        this.permissionService.getRoleAssignments(payload).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(roleAssignments => {
          this.roleAssignments = roleAssignments?.data;
          this.setTenantRoles();
        },
        (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        });
      } else {
        this.roleAssignments = data.data;
        this.setTenantRoles();
      }
    },
    (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  setTenantRoles(): void {
    this.tenantRoles = [];
    this.roleAssignments.forEach(role => {
      if (this.tenantRoles.find(item => item.tenant === role?.scope?.split('/')[2])) {
        const roles = this.tenantRoles.find(item => item.tenant === role?.scope?.split('/')[2])?.roles;
        this.tenantRoles.find(item => item.tenant === role?.scope?.split('/')[2]).roles = [...roles, role?.role];
      } else {
        const obj: any = {};
        obj.tenant = role?.scope?.split('/')[2] ? role?.scope?.split('/')[2] : role?.scope;
        obj.roles = [role.role];
        this.tenantRoles = [...this.tenantRoles, obj];
      }
    })
    this.isLoading = false;
  }

  goToGroups() {
    this.router.navigate(['/', this.authService.principal.provider, this.RouterSegments.GROUPS]);
  }

  deleteRole(event, tenantRole, role): void {
    const id = this.roleAssignments.find(item => item.principal.split('/')[2] === this.groupName && (item.scope.split('/')[2] === tenantRole.tenant || item.scope === tenantRole.tenant)
      && item.role === role)?.id;
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('GROUPS.DELETE_ROLE'),
        message: translate('GROUPS.DELETE_ROLE_MESSAGE'),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.showLoader = true;
        this.permissionService.deleteRoleAssignmentById(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data => {
          if (data) {
            setTimeout(() => {
              const message = translate('ROLES.ROLE_DELETED');
              const title = translate('ROLES.ROLES');
              this.toastrService.successMessage(message, title);
            }, 500);
            this.showLoader = false;
            this.getRoleAssignments();
            this.getAssignedPermissions();
          }
        },
        (error) => {
          this.showLoader = false;
          const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
          this.dialog.open(ErrorDialogComponent, {
            minHeight: '342px',
            width: '630px',
            panelClass: 'flex-dialog',
            data: {
              title:  translate('GROUPS.ROLE_DELETION_FAILED'),
              description: errorMessage,
              hideReportButton: true
            }
          });
        });
      }
    });
    event.stopPropagation();
  }

  viewPermissions(event, tenantRole, role): void {
    this.showLoader = true;
    this.permissionService.getRoleByName(role).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(roleDetails => {
      this.showLoader = false;
      roleDetails.tenant = tenantRole.tenant;
      this.dialog.open(ViewPermissionsComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '50vw',
        autoFocus: false,
        data: roleDetails
      });
    },
    (error) => {
      this.showLoader = false;
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
    event.stopPropagation();
  }

  assignRole(): void {
    if (!this.roleAssignments) {
      return;
    }
    const data = {
      name: this.groupName,
      description: this.group?.description,
      roleAssignments: this.roleAssignments
    };
    this.sidenavService.openInRightSidenav(AssignRoleComponent, data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formData) => {
        if (!formData) {
          return;
        } else {
          this.getRoleAssignments();
          this.getAssignedPermissions();
        }
      });
  }

  getAssignedPermissions(): void {
    let scope = '/global';
    this.authService.scope = scope;
    this.permissionsService.getAuthAssignedPermissions(scope)
    .pipe().subscribe(response => {
      this.authService.assignedPermissions = response.permissions;
    },
    (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }
}

